import { NavLink } from "react-router-dom";
import { useSession } from "../../app/session";
import { useEventContext } from "../../app/eventContext";
import { useResource } from "../../lib/hooks";
import { NAVIGATION, PORTAL_NAVIGATION, iconFor, type NavGroup } from "./navigation";

/**
 * The primary navigation.
 *
 * Entries the signed-in user cannot reach are not rendered at all. That is a
 * courtesy — the route guard and the API both refuse the same thing — but it
 * keeps the menu honest: everything shown here works when clicked.
 */
export function Sidebar({ open, onNavigate }: { open: boolean; onNavigate: () => void }) {
  const { user, canAny, hasModule } = useSession();
  const { currentId } = useEventContext();
  const branding = user?.tenant.branding;

  // A module switched off for the event in context leaves the menu while that event is in context, as its
  // settings say. Shares the event's cached detail, so switching a module updates the menu straight away.
  const eventModules = useResource<{ disabledModules?: string[] }>(
    ["event", currentId ?? ""],
    `/events/${currentId ?? ""}`,
    undefined,
    { enabled: Boolean(currentId) && !user?.isExternal },
  );
  const switchedOff = new Set(eventModules.data?.disabledModules ?? []);

  const groups: NavGroup[] = user?.isExternal ? PORTAL_NAVIGATION : NAVIGATION;

  // A portal user without a recorded audience is served as the client, as the API serves them.
  const audience = user?.portalAudience ?? "Client";

  const visible = groups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          canAny(...item.permissions) &&
          (!item.module || hasModule(item.module)) &&
          (!item.audiences || item.audiences.includes(audience)) &&
          !(item.needsEvent && item.module && switchedOff.has(item.module)),
      ),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <nav
      className={`sidebar${open ? " sidebar--open" : ""}`}
      aria-label="Main navigation"
    >
      <div className="sidebar__brand">
        {/*
          The official logo, used exactly as supplied. A tenant may supply its
          own for white-label deployments; neither is ever redrawn.
        */}
        <img
          className="sidebar__logo"
          src={branding?.logoUrl || "/brand/wolfray-ai-logo.png"}
          alt=""
        />
        {!branding?.hideWolfrayBranding && (
          <div className="sidebar__wordmark">
            WOLFRAY <em>AI</em>
          </div>
        )}
        <div className="sidebar__product">{branding?.productName || "EventOS"}</div>
      </div>

      <div className="sidebar__scroll">
        {visible.map((group) => (
          <div className="navgroup" key={group.label}>
            <div className="navgroup__label">{group.label}</div>
            {group.items.map((item) => {
              const IconComponent = iconFor(item.icon);

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/portal"}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `navlink${isActive ? " navlink--active" : ""}`
                  }
                >
                  <IconComponent className="navlink__icon" />
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        ))}
      </div>

      <div className="sidebar__foot">
        <div className="sidebar__tagline">
          <b>Build Extraordinary</b>
          <span>{branding?.tagline || "Events. Together."}</span>
        </div>
        <div className="sidebar__version">
          {!branding?.hideWolfrayBranding && (
            <b>
              WOLFRAY <em>AI</em>
            </b>
          )}
          <span>{branding?.productName || "EventOS"}</span>
          <span style={{ marginInlineStart: "auto" }}>v1.0.0</span>
        </div>
      </div>
    </nav>
  );
}
